// Date Table with Client Side Pagination, Search and Page Size

import React, { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { Loading, SearchInput } from "./";
import Icon from "../../assets/icons/Icon";
import SvgIcon from "../Components/UI/Svg";

const PAGE_SIZES = [50, 100, 200, 500];

const DataTable = ({
  columns,
  currentPage = 1,
  onPageChange,
  onRecordsPerPageChange,
  recordsPerPage = 50,
  searchTerm = "",
  onSearchChange,
  isSearchable = true,
  isPagination = true,
  isPageSize = true,
  isLoading = false,
  handleSearchKey,
  data = [],
  pages,
  handleDoubleClick,
}) => {
  // Global Search
  const [globalFilter, setGlobalFilter] = useState("");

  // Sorting
  const [sorting, setSorting] = useState([]);

  // Add Index Column
  const indexedColumns = useMemo(
    () => [
      {
        id: "index",
        header: "#",
        cell: ({ row }) => row.index + 1,
        enableSorting: false,
      },
      ...columns,
    ],
    [columns],
  );

  // Table Instance
  const table = useReactTable({
    columns: indexedColumns,
    data: data || [],

    state: {
      globalFilter,
      sorting,
    },

    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Disable sorting globally
    defaultColumn: {
      enableSorting: false,
    },
    autoResetPageIndex: false,
    ...(isPagination && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize: recordsPerPage,
        },
      },
    }),
  });

  // Current Page Size
  const getCurrentPageSizeValue = () => {
    const currentPageSize = table.getState().pagination?.pageSize;

    const totalRows = table.getCoreRowModel().rows.length;

    if (
      currentPageSize >= totalRows &&
      !PAGE_SIZES.slice(0, -1).includes(currentPageSize)
    ) {
      return "all";
    }

    return currentPageSize ?? recordsPerPage;
  };

  // Page Size Change
  const handlePageSizeChange = (e) => {
    const value = e.target.value;

    if (value === "all") {
      const totalRows = table.getCoreRowModel().rows.length;
      table.setPageSize(totalRows || 1);
    } else {
      table.setPageSize(Number(value));
    }

    if (onRecordsPerPageChange) {
      onRecordsPerPageChange(value === "all" ? data.length : Number(value));
    }

    if (onPageChange) {
      onPageChange(1);
    }
  };

  // Search
  const handleSearch = (val) => {
    setGlobalFilter(val);

    if (onSearchChange) {
      onSearchChange(val);
    }

    table.setPageIndex(0);
  };

  // handle double click
  const handleRowDoubleClick = (rowData) => {
    handleDoubleClick(rowData);
  };

  return (
    <>
      {/* Search */}
      {isSearchable && (
        <div className="table-searchbar">
          <div className="dataTables_input">
            <SearchInput
              value={globalFilter}
              onSearchChange={handleSearch}
              handleSearchKey={handleSearchKey}
            />

            {Boolean(globalFilter?.trim()) && (
              <button
                type="button"
                className="close_icon"
                onClick={() => handleSearch("")}
              >
                <Icon name="X" size={18} />
              </button>
            )}
          </div>
          <div className="dataTables_input">
            {(Boolean(data?.length && isPagination) || isPageSize) && (
              <select
                className="form-select"
                value={getCurrentPageSizeValue()}
                onChange={handlePageSizeChange}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size === "all" ? "All rows" : `${size} rows`}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {data ? (
        <div className="table-responsive">
          <table className="report-card-box">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={index}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort()
                          ? "cursor-pointer"
                          : "cursor-default"
                      }
                    >
                      {header.isPlaceholder ? null : (
                        <div className="d-flex align-items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                          {header.column.getCanSort() && (
                            <span>
                              {{
                                asc: <Icon name="ChevronUp" size={16} />,
                                desc: <Icon name="ChevronDown" size={16} />,
                              }[header.column.getIsSorted()] ?? (
                                <Icon name="ChevronsUpDown" size={16} />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={indexedColumns.length} className="text-center">
                    <Loading />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr className="not_rows_tr">
                  <td colSpan={indexedColumns.length} className="text-center">
                    <div className="not_rows">
                      <SvgIcon className="not_icon" name="notData" />
                      No rows to show
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => {
                  const isActive = row.original?.isActive;

                  return (
                    <tr
                      key={index}
                      className={isActive ? "" : ""}
                      onDoubleClick={() => handleRowDoubleClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell, index1) => (
                        <td key={index1}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="not_rows">No Record</div>
      )}

      {/* Pagination */}
      {data && (
        <div className="page-table">
          <div className="page-table__info">
            <span className="dt-total">
              {table.getFilteredRowModel().rows.length} Rows
            </span>
          </div>

          {isPagination && !isLoading && (
            <div className="page-table__pagination">
              <ul className="pagination">
                {/* First */}
                <li
                  className={`page-item ${
                    !table.getCanPreviousPage() ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <Icon name="ChevronsLeft" size={18} />
                  </button>
                </li>

                {/* Previous */}
                <li
                  className={`page-item ${
                    !table.getCanPreviousPage() ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <Icon name="ChevronLeft" size={18} />
                  </button>
                </li>

                {/* Current Page */}
                <li className="page-item active">
                  <button className="page-link">
                    {table.getState().pagination.pageIndex + 1} /{" "}
                    {table.getPageCount()}
                  </button>
                </li>

                {/* Next */}
                <li
                  className={`page-item ${
                    !table.getCanNextPage() ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    <Icon name="ChevronRight" size={18} />
                  </button>
                </li>

                {/* Last */}
                <li
                  className={`page-item ${
                    !table.getCanNextPage() ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                  >
                    <Icon name="ChevronsRight" size={18} />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DataTable;
